import { Box, Chip, Stack } from '@mui/material';
import Image from 'next/image';
import React, { FC } from 'react';

import { AbgTypography, DropOff, KeyDropLocation } from '@abg/kit-base';
import { spacing } from '@abg/kit-base/lib/shared/constants';

import { useBookingWidgetContext } from '../../../../organisms/BookingWidget/hooks';
import {
  AF_DELIVERY_KEY,
  resolveAfTier,
} from '../../../../shared/utils/avis-first';

import {
  LocationFeatureChip,
  LocationFeatureChipKey,
} from './location-features.types';
import * as styles from './LocationFeatures.styling';

import avisFirstLogo from '../../../../../assets/avis-first-logo.svg';

const chipOptions: Omit<LocationFeatureChip, 'label'>[] = [
  {
    key: 'is24hoursDropOffAvailable',
    icon: <DropOff />,
  },
  {
    key: 'isKeyDropLocation',
    icon: <KeyDropLocation />,
  },
];

type LocationFeaturesProperties = {
  features: Partial<Record<LocationFeatureChipKey, boolean>>;
  featureTranslations: Partial<Record<LocationFeatureChipKey, string>>;
  featureTestIdBuilder?: (item: LocationFeatureChip) => string;
  dataTestId?: string;
  siteCodeGroup?: string;
  isAvisFirstLocation?: boolean;
  isAvisFirstLiteLocation?: boolean;
  deliveryLabels?: Partial<Record<string, string>>;
  locationType?: 'pickup' | 'dropoff';
};

export const LocationFeatures: FC<LocationFeaturesProperties> = ({
  features,
  featureTranslations = {},
  featureTestIdBuilder = (chip) => `location-features-chip-${chip.key}`,
  dataTestId = '',
  siteCodeGroup,
  isAvisFirstLocation,
  isAvisFirstLiteLocation,
  deliveryLabels = {},
  locationType,
}) => {
  const {
    isAFLocalMarketChangesEnabled,
    isAFLocalMarketStepOneOtherLocationLabelEnabled,
  } = useBookingWidgetContext();

  const applicableFeatures = new Set(
    Object.entries(features)
      .filter(([, isAvailableFeature]) => isAvailableFeature)
      .map(([key]) => key as LocationFeatureChipKey),
  );

  const locationFeaturesChips: LocationFeatureChip[] = chipOptions
    .filter((chip) => applicableFeatures.has(chip.key))
    .map((chip) => ({
      ...chip,
      label: featureTranslations[chip.key] ?? chip.key,
    }));

  const tier = resolveAfTier({
    isAvisFirst: !!isAvisFirstLocation,
    isAvisFirstLite: !!isAvisFirstLiteLocation,
    isAirport: siteCodeGroup?.toLowerCase() === 'airport',
  });

  const deliveryKey = AF_DELIVERY_KEY[tier];
  const deliveryLabel = deliveryKey ? (deliveryLabels[deliveryKey] ?? '') : '';

  const showAvisFirstBlock =
    isAFLocalMarketChangesEnabled &&
    isAFLocalMarketStepOneOtherLocationLabelEnabled &&
    locationType === 'pickup' &&
    isAvisFirstLocation &&
    !!deliveryLabel;

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          gap: '12px',
          alignItems: 'center',
          marginTop: '16px',
        }}
      >
        {showAvisFirstBlock ? (
          <Box sx={{ ...styles.ChipWrapper, alignItems: 'center', gap: '8px' }}>
            <Image
              data-testid="avis-first-logo-bookingwidget-flyout-modal"
              style={{
                width: '58px',
                height: '8px',
              }}
              src={avisFirstLogo}
              alt="avis-first-logo"
              width={0}
              height={0}
            />
            <AbgTypography variant="caption">{deliveryLabel}</AbgTypography>
          </Box>
        ) : null}
        {locationFeaturesChips.length > 0 ? (
          <Stack
            style={{ gap: spacing['3xs'] }}
            direction="row"
            flexWrap="wrap"
            data-testid={dataTestId}
          >
            {locationFeaturesChips.map((chip) => (
              <Chip
                key={chip.key}
                icon={chip.icon}
                label={chip.label}
                sx={styles.ChipWrapper}
                data-testid={featureTestIdBuilder(chip)}
              />
            ))}
          </Stack>
        ) : null}
      </Box>
    </>
  );
};
